import Vue from "vue";

import "@/plugins/sentry";
import '@fontsource-variable/inter';
import "@fontsource/roboto";
import "@fontsource/roboto-mono";
import store from "@/store";
import router from "@/router";
import vuetify from "@/plugins/vuetify";
import client from "@/plugins/axios";
import i18n from "@/plugins/i18n";
import "@/plugins/meta";
import "@/plugins/validate";
import "@/plugins/snotify";
import "@/plugins/moments";
import "@/plugins/directives";

import { ConfigPlugin, config } from "@/config";
import App from "@/App.vue";
import "leaflet/dist/leaflet.css";
import {
  currentToken as currentKeycloakToken,
  enabled as keycloakEnabled,
  hasStoredSession as hasStoredKeycloakSession,
  initialize as initializeKeycloak,
  pendingNext as pendingKeycloakNext,
  setPendingNext as setPendingKeycloakNext,
} from "@/auth/keycloak";

import { Icon } from "leaflet";
delete Icon.Default.prototype._getIconUrl;

Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

/* config */

Vue.use(ConfigPlugin);
Vue.config.productionTip = false;

/* webcomponents have to be defined as ignoredElement so that the 
   compiler does not complain.
*/
Vue.config.ignoredElements = ["gfo-form"];

function mountApp() {
  new Vue({
    router,
    store,
    vuetify,
    i18n,
    el: "#app",
    render: (h) => h(App),
    created() {
      // required for global Vue instance access
      window.vm = this;
    },
  });
}

async function initializeSession() {
  const ssoCallback =
    keycloakEnabled() && window.location.pathname === "/sso-callback";
  const restoringSso =
    !ssoCallback &&
    store.state.authProvider !== "legacy" &&
    hasStoredKeycloakSession();

  if (restoringSso)
    setPendingKeycloakNext(
      window.location.pathname + window.location.search + window.location.hash
    );

  if (ssoCallback || restoringSso) {
    const authenticated = await initializeKeycloak();
    if (!authenticated || currentKeycloakToken() == null)
      throw new Error("Keycloak did not return an authenticated session");
    store.commit("setSsoToken", currentKeycloakToken());
    if (restoringSso) setPendingKeycloakNext(null);
  }

  return client
    .get("profile", { disableDefaultErrorHandler: true })
    .then((response) => {
      store.commit("setSession", response.data);
    });
}

function redirectToLogin() {
  const ssoNext = pendingKeycloakNext();
  store.commit("resetSession");
  const next =
    ssoNext ||
    (window.location.pathname !== "/" &&
    window.location.pathname !== "/login" &&
    window.location.pathname !== "/sso-callback"
      ? window.location.pathname + window.location.search + window.location.hash
      : null);
  const query = next ? "?next=" + encodeURIComponent(next) : "";
  window.location.replace("/login" + query);
}

function errorMessage(error) {
  if (error == null) return "Keycloak authentication failed";
  if (typeof error === "string") return error;
  return error.message || error.error_description || error.error || String(error);
}

/* initialize store before Vue, but ignore login for SAK/cidaas redirects */
if (
  config.mandant == "sak" &&
  (window.location.pathname == "/login-callback" ||
    window.location.pathname.indexOf("/staff") == 0)
) {
  mountApp();
} else {
  initializeSession()
    .catch((error) => {
      console.error("Session initialization failed", error);
      if (window.location.pathname === "/sso-callback") {
        store.commit("resetSession");
        store.commit("setSystemError", {
          title: "SSO login failed",
          msg: errorMessage(error),
          icon: "mdi-lock-alert",
        });
      } else if (window.location.pathname === "/login") {
        store.commit("resetSession");
      } else {
        redirectToLogin();
      }
    })
    .finally(mountApp);
}
